import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { ASSISTANT_ACTION_STATUS } from '../../utils/assistantStatus';

const money = (amount, currency) => {
  if (amount == null || !currency) return '—';
  try {
    return new Intl.NumberFormat('en', { style: 'currency', currency }).format(amount);
  } catch {
    return `${amount} ${currency}`;
  }
};

const validUntil = (iso) => {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

const Row = ({ label, children }) => (
  <div className="flex justify-between gap-3 text-sm">
    <dt className="text-muted-foreground shrink-0">{label}</dt>
    <dd className="text-right break-words min-w-0">{children}</dd>
  </div>
);

const Outcome = ({ result }) => {
  const { status, order_number: orderNumber, awb, tracking_url: trackingUrl, error } = result;
  if (status === ASSISTANT_ACTION_STATUS.COMPLETED) {
    return (
      <div className="rounded-md border border-border p-3 text-sm space-y-1" role="status">
        <p className="flex items-center gap-2 font-medium">
          <CheckCircle2 className="h-4 w-4" aria-hidden="true" /> Order created and shipment booked
        </p>
        <p>Order number: {orderNumber}</p>
        <p>Tracking number (AWB): {awb}</p>
        {typeof trackingUrl === 'string' && trackingUrl.startsWith('https://') && (
          <a href={trackingUrl} target="_blank" rel="noopener noreferrer" className="underline">
            Track shipment
          </a>
        )}
      </div>
    );
  }
  if (status === ASSISTANT_ACTION_STATUS.SHIPMENT_FAILED) {
    return (
      <div className="rounded-md border border-border p-3 text-sm space-y-1" role="alert">
        <p className="flex items-center gap-2 font-medium">
          <AlertTriangle className="h-4 w-4" aria-hidden="true" /> Order {orderNumber} was created, but the shipment failed
        </p>
        <p className="break-words">{error}</p>
        <p className="text-muted-foreground">Say &quot;retry&quot; in the chat, or name another carrier.</p>
      </div>
    );
  }
  if (status === ASSISTANT_ACTION_STATUS.EXECUTING) {
    return (
      <div className="rounded-md border border-border p-3 text-sm flex items-center gap-2" role="status">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Still in progress. {error}
      </div>
    );
  }
  return (
    <div className="rounded-md border border-border p-3 text-sm space-y-1" role="alert">
      <p className="flex items-center gap-2 font-medium">
        <AlertTriangle className="h-4 w-4" aria-hidden="true" /> Nothing was created
      </p>
      <p className="break-words">{error}</p>
    </div>
  );
};

/** Renders only from action.summary (never from chat text). Confirm is a button, never a chat message. */
const AssistantSummaryCard = ({ action, onConfirm, onCancel, busy = false, result = null }) => {
  const s = action.summary;
  const until = validUntil(action.expires_at);
  return (
    <section aria-label="Order summary" className="rounded-lg border border-border bg-card p-4 space-y-3">
      <dl className="space-y-1.5">
        <Row label="Recipient">{s.recipient_name}</Row>
        <Row label="Phone">{s.phone}</Row>
        <Row label="Address">{s.address}</Row>
        <Row label="Order type">{s.order_type}</Row>
        <Row label="Carrier">
          {s.carrier} {s.service}
        </Row>
        <Row label="Shipping">{money(s.shipping_price, s.shipping_currency)}</Row>
        <Row label="Delivery">{s.eta}</Row>
        <Row label="Declared value">{money(s.declared_value, s.declared_currency)}</Row>
      </dl>
      <ul className="space-y-1 text-sm border-t border-border pt-2">
        {s.items.map((item, i) => (
          <li key={`${item.name}-${item.size}-${i}`} className="flex justify-between gap-3">
            <span className="min-w-0 break-words">
              {item.quantity} × {item.name} ({item.size})
            </span>
            <span className="shrink-0">{money(item.unit_price, s.declared_currency)}</span>
          </li>
        ))}
      </ul>
      {s.new_influencer && (
        <p className="text-sm">A new influencer profile will be created: @{s.new_influencer.ig_handle}</p>
      )}
      {s.non_commercial && (
        <p className="text-sm">Declared as a non-commercial gift for customs.</p>
      )}
      {result ? (
        <Outcome result={result} />
      ) : (
        <>
          {until && <p className="text-xs text-muted-foreground">Valid until {until}</p>}
          <div className="flex gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => onConfirm(action.id)}
              className="flex-1 rounded-md bg-primary text-primary-foreground px-3 py-2 text-sm font-medium disabled:opacity-50"
            >
              Confirm
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => onCancel(action.id)}
              className="rounded-md border border-border px-3 py-2 text-sm disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </>
      )}
    </section>
  );
};

export default AssistantSummaryCard;
